import { Box } from '@eatclubcode/ec-component-library';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import {
  getLifetimeDetailsAction,
  getPauseFeesAction,
  getRestaurantPauseProjectedAction,
  saveRestaurantPauseAction,
} from '../../actions/cancellationAction';
import { fetchPotentialRevenuesAction } from '../../actions/potentialRevenuesAction';
import { fetchNetBalanceByRestIdAction } from '../../actions/settlementsAction';
import { CANCEL_REASON_TRACKING } from '../../constants';
import { devLog } from '../../utils';
import { trackDetailedEvent } from '../../utils/analytics';
import { isEmpty } from '../../utils/baseHelpers';
import { formatDate } from '../../utils/helpers';
import CancelLandingPage from './CancelLandingPage';
import useStyles from './CancelMembershipStyles';
import CancelReasonPage from './CancelReasonPage';
import ExtraWeekPage from './ExtraWeekPage';
import SchedulePage from './SchedulePage';
import SuccessPage from './SuccessPage';

const CancelMembership = ({
  restId,
  userId,
  userType,
  getLifetimeDetails,
  getPauseFees,
  getRestaurantPauseProjected,
  lifetimeDetails,
  pauseFees,
  fetchPotentialRevenues,
  potentialRevenues,
  saveRestaurantPause,
  cancellation,
  pauseProjection,
  fetchNetBalanceByRestId,
  netBalance,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [reason, setReason] = useState(null);
  const [reasonData, setReasonData] = useState({}); // All of the data we capture for the reason they are leaving
  const [acceptedOffer, setAcceptedOffer] = useState(false); // Whether they accepted to stay another week
  const [callDate, setCallDate] = useState(null); // Date for AM to call
  const scrollRef = useRef(null);

  useEffect(() => {
    getLifetimeDetails();
    getPauseFees();
    getRestaurantPauseProjected();
    fetchPotentialRevenues();
    fetchNetBalanceByRestId();

    // Also track page view
    trackDetailedEvent('Cncl Flow Step 1 (Retention Messaging)');
  }, []);

  const venueData = {
    loading: lifetimeDetails.pending || pauseFees?.pending,
    totalRevenue: lifetimeDetails.data?.totalRevenue,
    totalCustomers: lifetimeDetails.data?.totalUsers,
    projectedRevenue:
      (pauseProjection?.data?.revenue ?? 0) + (potentialRevenues?.data?.offPeakOptimized ?? 0),
    projectedCustomers:
      (pauseProjection?.data?.users ?? 0) + (potentialRevenues?.data?.offPeakCustomersUpper ?? 0),
    hasDebt: netBalance?.data?.netBalance < 0,
  };

  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false); // Internal knowledge of when the user triggered the save action

  // Scroll to top when changing screen
  useEffect(() => {
    scrollRef.current.scrollTo(0, 0);
  }, [page]);

  // Handle successful submission
  useEffect(() => {
    devLog('info', 'CancelMembership Save', 'cancellation changed', cancellation, saving.current);
    if (saving.current && cancellation?.pending === false && isEmpty(cancellation?.error)) {
      devLog('info', 'CancelMembership', 'Successfully cancelled');
      saving.current = false;
      setIsSaving(false);
      setPage(page + 1);
    }

    // Failure
    if (saving.current && cancellation?.pending === false && !isEmpty(cancellation?.error)) {
      devLog('info', 'CancelMembership', 'failed to cancel');
      saving.current = false;
      setIsSaving(false);
    }
  }, [cancellation?.pending, cancellation?.error]);

  // Extra week may not always show
  const saveHighValue =
    (reason === 'financial_difficulty' ||
      reason === 'not_getting_value' ||
      reason === 'eatclub_customer_issues') &&
    lifetimeDetails?.data?.highValueVenue;
  const scheduleMeeting = saveHighValue && acceptedOffer;

  // List of steps that changes depending on what the user enters
  const pages = useMemo(
    () =>
      [
        'cancel',
        'cancelReason',
        saveHighValue ? 'extraWeek' : null,
        scheduleMeeting ? 'schedule' : null,
        'success',
      ].filter((item) => !!item),
    [saveHighValue, scheduleMeeting],
  );
  const totalSteps = pages.length;
  const activePage = pages?.[page];

  const churnState = acceptedOffer ? 'saved' : reason === 'pause' ? 'paused' : 'churned';

  const concatIfSet = (str, title) => {
    if (isEmpty(str)) {
      return '';
    }

    return `${title}: ${str}`;
  };

  const handleSetReason = (newReason) => {
    setReason(newReason);

    trackDetailedEvent(`Reason selected`, {
      cancel_reason: CANCEL_REASON_TRACKING?.[newReason] ?? 'None',
    });
  };

  // Go forward or back a step
  const handleUpdateStep = (newStep, newReasonData = {}) => {
    // Submit if this was the final step
    if (pages?.[newStep] === 'success') {
      // We take the new data as a parameter to avoid a race condition if it tries to set and submit at the same time
      const allReasonData = {
        ...reasonData,
        ...newReasonData,
      };

      // Prepare the data for saving. Different reasons send different data
      let saveData = {
        amCallDates: callDate ? [dayjs(callDate).format('YYYY-MM-DD')] : null,
        amCallDatePeriod: callDate ? 'am' : null, // am/pm if call date set
        offerAccepted: acceptedOffer,
        hasDebt: venueData?.hasDebt,
      };
      switch (reason) {
        case 'sold_business':
          saveData = {
            ...saveData,
            reason,
            reasonDetails: {
              type: 'boolean',
              description: `Are you interested in a referral bonus for getting the new owner to keep using EatClub?`,
              value: `${allReasonData?.referralBonus}`,
            },
            // Combine the different free text fields into one
            reasonFreeText: [
              concatIfSet(allReasonData?.notes, 'Pause notes'),
              concatIfSet(allReasonData?.newOwnerDetails, 'New owner details'),
            ]
              .filter((note) => !!note)
              .join('\n'),
          };
          break;
        case 'closed_permanently':
          saveData = {
            ...saveData,
            reason,
            email: allReasonData?.emailAddress,
          };
          break;
        case 'pause_membership':
          const getResumeDate = () => {
            if (allReasonData?.pauseWeeks === 'custom') {
              if (allReasonData?.customDate) {
                return formatDate(
                  dayjs(allReasonData?.customDate).subtract(1, 'day'),
                  'YYYY-MM-DD',
                );
              }

              return null;
            }
            return formatDate(
              dayjs().add(allReasonData?.pauseWeeks, 'weeks').subtract(1, 'day').startOf('day'),
              'YYYY-MM-DD',
            );
          };
          saveData = {
            ...saveData,
            reason,
            timeframe: getResumeDate(),
            reasonFreeText: allReasonData?.notes,
          };
          break;
        case 'not_getting_value':
          saveData = {
            ...saveData,
            reason: allReasonData?.valueReasons,
            reasonFreeText: allReasonData?.reconsiderNotes,
          };
          break;
        case 'financial_difficulty':
          saveData = {
            ...saveData,
            reason,
            reasonFreeText: allReasonData?.notes,
          };
          break;
        case 'technical_issues':
          saveData = {
            ...saveData,
            reason,
            reasonDetails: {
              type: 'boolean',
              description:
                'If we could resolve these issues immediately, would you re-consider staying with EatClub?',
              value: `${allReasonData?.issueResolution}`,
            },
            reasonFreeText: allReasonData?.issuesNotes,
          };
          break;
        case 'eatclub_customer_issues':
          saveData = {
            ...saveData,
            reason,
            reasonFreeText: allReasonData?.issuesNotes,
          };
          break;
        default:
          break;
      }

      // trackEvent(`button_click: pause_complete`);
      if (venueData?.hasDebt) {
        trackDetailedEvent(`Cncl_debt_intervention`, { reason, accepted_offer: acceptedOffer });
      } else {
        trackDetailedEvent(`Cancellation confirmed`, { reason, accepted_offer: acceptedOffer });
      }

      saving.current = true; // ref is used to prevent race conditions
      setIsSaving(true); // state is used for ui
      saveRestaurantPause(saveData);
      return;
    }

    // Track page view update
    if (pages?.[newStep] === 'cancelReason') {
      trackDetailedEvent(`cncl_step2_reason_capture`, {
        current_page: activePage,
        next_page: pages?.[newStep],
        reason,
      });
    }

    if (pages?.[newStep] === 'extraWeek') {
      trackDetailedEvent(`cncl_1_week_offer`, {
        current_page: activePage,
        next_page: pages?.[newStep],
        reason,
      });
    }

    if (pages?.[newStep] === 'schedule') {
      trackDetailedEvent(`am_booking_screen`, {
        current_page: activePage,
        next_page: pages?.[newStep],
        reason,
      });
    }

    // trackDetailedEvent(`Cancellation step progressed`, {
    //   currentPage: activePage,
    //   nextPage: pages?.[newStep],
    //   reason,
    // });

    // Otherwise continue
    setPage(newStep);
  };

  const handleExit = () => {
    trackDetailedEvent(`Cancellation exited`, { current_page: activePage, reason });
    history.push('/insights');
  };

  const handleAcceptOffer = (newStep) => {
    trackDetailedEvent('Cancellation offer accepted', { current_page: activePage, reason });
    setAcceptedOffer(true);
    setPage(newStep);
  };

  const handleFinish = () => {
    trackDetailedEvent(`Cancellation completed`, { current_page: activePage });
    history.push('/offers');
  };

  return (
    <>
      <Box className={classes.cancelMembership} ref={scrollRef}>
        {activePage === 'cancel' && (
          <CancelLandingPage
            venueData={venueData}
            currentStep={page}
            totalSteps={totalSteps}
            isSaving={isSaving}
            onContinue={() => handleUpdateStep(page + 1)}
            onExit={handleExit}
          />
        )}
        {activePage === 'cancelReason' && (
          <CancelReasonPage
            currentStep={page}
            totalSteps={totalSteps}
            isSaving={isSaving}
            reason={reason}
            setReason={handleSetReason}
            onContinue={(newReasonData) => {
              handleUpdateStep(page + 1, newReasonData);
              setReasonData(newReasonData);
            }}
            onExit={handleExit}
          />
        )}
        {activePage === 'extraWeek' && (
          <ExtraWeekPage
            currentStep={page}
            totalSteps={totalSteps}
            isSaving={isSaving}
            onContinue={() => {
              trackDetailedEvent('Cancellation offer rejected');
              handleUpdateStep(page + 1);
            }}
            onExit={handleExit}
            onAccept={() => {
              handleAcceptOffer(page + 1);
            }}
          />
        )}
        {activePage === 'schedule' && (
          <SchedulePage
            currentStep={page}
            totalSteps={totalSteps}
            isSaving={isSaving}
            onContinue={() => handleUpdateStep(page + 1)}
            callDate={callDate}
            setCallDate={(value) => {
              setCallDate(value);
              trackDetailedEvent('Account manager call date selected', {
                date: dayjs(value).format('YYYY-MM-DD'),
              });
            }}
            onExit={handleExit}
          />
        )}
        {activePage === 'success' && (
          <SuccessPage
            currentStep={page}
            totalSteps={totalSteps}
            isSaving={false}
            onContinue={handleFinish}
            churnState={churnState}
            reason={reason}
            reasonData={reasonData}
            acceptedOffer={acceptedOffer}
            hasDebt={venueData?.hasDebt}
          />
        )}
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  restId: state.restaurantActive.restaurant.objectId,
  userId: state.user.userInfo.objectId,
  userType: state.user.userInfo.userType,
  lifetimeDetails: state.lifetimeDetails,
  pauseFees: state.pauseFees,
  pauseProjection: state.pauseProjection,
  potentialRevenues: state.potentialRevenues,
  cancellation: state.cancellation,
  netBalance: state.netBalance,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getLifetimeDetails: getLifetimeDetailsAction,
      getPauseFees: getPauseFeesAction,
      getRestaurantPauseProjected: getRestaurantPauseProjectedAction,
      fetchPotentialRevenues: fetchPotentialRevenuesAction,
      saveRestaurantPause: saveRestaurantPauseAction,
      fetchNetBalanceByRestId: fetchNetBalanceByRestIdAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(CancelMembership);
